import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Collapse,
  Tooltip,
  useMediaQuery,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  Add,
  Send,
  Psychology,
  ChatBubbleOutline,
  Delete,
  Menu as MenuIcon,
  ExpandMore,
  ExpandLess,
  CalendarMonth,
  Close,
  Settings
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import moment from 'moment';
import { useSelector } from 'react-redux';
import axios from '../../resources/axios';
import { selectAuth } from '../../redux/slices/authSlice';

const DRAWER_WIDTH = 280;
const STORAGE_KEY = 'ai_analytics_conversations';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  dataUsed?: Record<string, any>;
  timestamp: string;
  dateFrom?: string;
  dateTo?: string;
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  createdAt: string;
  updatedAt: string;
}

const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: 'calc(100vh - 180px)',
  overflow: 'hidden',
  backgroundColor: theme.palette.mode === 'light' ? '#f7f7f8' : '#1a1a1a',
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'light' 
    ? '0 2px 8px rgba(0,0,0,0.08)' 
    : '0 2px 8px rgba(0,0,0,0.3)'
}));

const Sidebar = styled(Box)(({ theme }) => ({
  width: DRAWER_WIDTH,
  backgroundColor: theme.palette.mode === 'light' ? '#f0f0f0' : '#202123',
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  borderTopLeftRadius: 16,
  borderBottomLeftRadius: 16
}));

const ChatArea = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden'
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2)
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser'
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  maxWidth: '80%',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  backgroundColor: isUser 
    ? (theme.palette.mode === 'light' ? '#5073F8' : '#5073F8')
    : (theme.palette.mode === 'light' ? '#ffffff' : '#2d2d30'),
  color: isUser ? '#ffffff' : theme.palette.text.primary,
  padding: theme.spacing(2),
  borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
  boxShadow: theme.palette.mode === 'light' 
    ? '0 1px 2px rgba(0,0,0,0.1)' 
    : '0 1px 3px rgba(0,0,0,0.3)',
  '& p': {
    margin: 0,
    lineHeight: 1.6
  },
  '& ul, & ol': {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2.5)
  },
  '& li': {
    marginBottom: theme.spacing(0.5)
  },
  '& h1, & h2, & h3': {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1)
  },
  '& code': {
    backgroundColor: isUser ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: '0.9em'
  },
  '& pre': {
    backgroundColor: isUser ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)',
    padding: theme.spacing(1.5),
    borderRadius: 8,
    overflow: 'auto',
    '& code': {
      backgroundColor: 'transparent',
      padding: 0
    }
  }
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#2d2d30',
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottomRightRadius: 16
}));

const InputWrapper = styled(Box)(({ theme }) => ({
  maxWidth: 900,
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1)
}));

const DateRangeBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#1e1e1e',
  borderRadius: 8,
  marginBottom: theme.spacing(1)
}));

const DataUsedBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
  backgroundColor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)',
  borderRadius: 8,
  fontSize: 12
}));

const JsonPre = styled('pre')(({ theme }) => ({
  margin: 0,
  fontSize: 11,
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: 200,
  overflow: 'auto'
}));

const EmptyState = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center'
}));

const ConversationItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'selected'
})<{ selected?: boolean }>(({ theme, selected }) => ({
  borderRadius: 8,
  margin: '2px 8px',
  backgroundColor: selected 
    ? (theme.palette.mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)')
    : 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)'
  }
}));

const generateId = () => Math.random().toString(36).substring(2) + Date.now().toString(36);

const AiAnalyticsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const auth = useSelector(selectAuth);
  
  const isSuperAdmin = auth.info?.type === 'superAdmin';
  
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [dateFrom, setDateFrom] = useState(moment().subtract(7, 'days').format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(moment().format('YYYY-MM-DD'));
  const [loading, setLoading] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [expandedDataIds, setExpandedDataIds] = useState<Set<string>>(new Set());
  
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [promptUpdatedAt, setPromptUpdatedAt] = useState<string | null>(null);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);

  const activeConversation = conversations.find(c => c.id === activeConversationId);
  
  useEffect(() => {
    if (isSuperAdmin && promptDialogOpen) {
      fetchSystemPrompt();
    }
  }, [isSuperAdmin, promptDialogOpen]);
  
  const fetchSystemPrompt = async () => {
    setLoadingPrompt(true);
    try {
      const response = await axios.get('/ai/system_prompt');
      setSystemPrompt(response.data.prompt || '');
      setPromptUpdatedAt(response.data.updatedAt || null);
    } catch (err) {
      console.error('Failed to fetch system prompt:', err);
    } finally {
      setLoadingPrompt(false);
    }
  };
  
  const handleSavePrompt = async () => {
    setSavingPrompt(true);
    try {
      const response = await axios.put('/ai/system_prompt', {
        prompt: systemPrompt
      });
      setPromptUpdatedAt(response.data.updatedAt || null);
      setPromptDialogOpen(false);
    } catch (err) {
      console.error('Failed to save system prompt:', err);
    } finally {
      setSavingPrompt(false);
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setConversations(parsed);
        if (parsed.length > 0) {
          setActiveConversationId(parsed[0].id);
        }
      } catch (e) {
        console.error('Failed to parse saved conversations:', e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations));
  }, [conversations]);

  const handleDateFromChange = (newDateFrom: string) => {
    setDateFrom(newDateFrom);
    const maxDate = moment(newDateFrom).add(60, 'days');
    const currentTo = moment(dateTo);
    if (currentTo.isAfter(maxDate)) {
      setDateTo(maxDate.format('YYYY-MM-DD'));
    }
  };

  const handleDateToChange = (newDateTo: string) => {
    const minDate = moment(newDateTo).subtract(60, 'days');
    const currentFrom = moment(dateFrom);
    if (currentFrom.isBefore(minDate)) {
      setDateFrom(minDate.format('YYYY-MM-DD'));
    }
    setDateTo(newDateTo);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeConversation?.messages]);

  const createNewConversation = () => {
    const newConv: Conversation = {
      id: generateId(),
      title: 'Нова бесіда',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setConversations(prev => [newConv, ...prev]);
    setActiveConversationId(newConv.id);
    setMobileDrawerOpen(false);
  };

  const deleteConversation = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConversations(prev => {
      const remaining = prev.filter(c => c.id !== id);
      if (activeConversationId === id) {
        setActiveConversationId(remaining.length > 0 ? remaining[0].id : null);
      }
      return remaining;
    });
  };

  const selectConversation = (id: string) => {
    setActiveConversationId(id);
    setMobileDrawerOpen(false);
  };

  const toggleDataExpanded = (messageId: string) => {
    setExpandedDataIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    let conversationId = activeConversationId;
    
    if (!conversationId) {
      const newConv: Conversation = {
        id: generateId(),
        title: input.trim().substring(0, 50) + (input.trim().length > 50 ? '...' : ''),
        messages: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setConversations(prev => [newConv, ...prev]);
      conversationId = newConv.id;
      setActiveConversationId(newConv.id);
    }

    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString(),
      dateFrom,
      dateTo
    };

    setConversations(prev => prev.map(conv => {
      if (conv.id === conversationId) {
        const isFirstMessage = conv.messages.length === 0;
        return {
          ...conv,
          title: isFirstMessage ? input.trim().substring(0, 50) + (input.trim().length > 50 ? '...' : '') : conv.title,
          messages: [...conv.messages, userMessage],
          updatedAt: new Date().toISOString()
        };
      }
      return conv;
    }));

    setInput('');
    setLoading(true);

    try {
      const response = await axios.post('/ai/query', {
        question: input.trim(),
        dateFrom: moment(dateFrom).startOf('day').toISOString(),
        dateTo: moment(dateTo).endOf('day').toISOString()
      });

      const assistantMessage: Message = {
        id: generateId(),
        role: 'assistant',
        content: response.data.answer,
        dataUsed: response.data.dataUsed,
        timestamp: new Date().toISOString()
      };

      setConversations(prev => prev.map(conv => {
        if (conv.id === conversationId) {
          return {
            ...conv,
            messages: [...conv.messages, assistantMessage],
            updatedAt: new Date().toISOString()
          };
        }
        return conv;
      }));
    } catch (err: any) {
      console.error('AI query error:', err);
      const errorMessage: Message = {
        id: generateId(),
        role: 'assistant',
        content: '❌ Сталася помилка при запиті до AI. Спробуйте ще раз.',
        timestamp: new Date().toISOString()
      };

      setConversations(prev => prev.map(conv => {
        if (conv.id === conversationId) {
          return {
            ...conv,
            messages: [...conv.messages, errorMessage],
            updatedAt: new Date().toISOString()
          };
        }
        return conv;
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const sidebarContent = (
    <Sidebar>
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Add />}
          onClick={createNewConversation}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            justifyContent: 'flex-start',
            py: 1.5
          }}
        >
          Нова бесіда
        </Button>
        {isSuperAdmin && (
          <Button
            fullWidth
            variant="text"
            startIcon={<Settings />}
            onClick={() => setPromptDialogOpen(true)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              justifyContent: 'flex-start',
              py: 1,
              mt: 1,
              color: 'text.secondary'
            }}
          >
            Системний промпт
          </Button>
        )}
      </Box>
      
      <Divider />
      
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        <List disablePadding>
          {conversations.map(conv => (
            <ConversationItem
              key={conv.id}
              selected={conv.id === activeConversationId}
              onClick={() => selectConversation(conv.id)}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <ChatBubbleOutline fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={conv.title}
                primaryTypographyProps={{
                  noWrap: true,
                  fontSize: 14
                }}
                secondary={moment(conv.updatedAt).format('DD.MM.YYYY HH:mm')}
                secondaryTypographyProps={{
                  fontSize: 11
                }}
              />
              <IconButton 
                size="small" 
                onClick={(e) => deleteConversation(conv.id, e)}
                sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </ConversationItem>
          ))}
        </List>
        
        {conversations.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              Немає збережених бесід
            </Typography>
          </Box>
        )}
      </Box>
    </Sidebar>
  );

  return (
    <PageContainer>
      {isMobile ? (
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          PaperProps={{ sx: { width: DRAWER_WIDTH } }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        sidebarContent
      )}

      <ChatArea>
        {isMobile && (
          <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => setMobileDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
            <Typography variant="subtitle1" noWrap sx={{ flex: 1 }}>
              {activeConversation?.title || 'AI Аналітика'}
            </Typography>
          </Box>
        )}

        <MessagesContainer>
          {!activeConversation || activeConversation.messages.length === 0 ? (
            <EmptyState>
              <Psychology sx={{ fontSize: 80, color: 'text.disabled', mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                AI Аналітика
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 500 }}>
                Задайте питання про роботу самокатів, поїздки, користувачів або інші аспекти бізнесу. 
                AI проаналізує дані за вибраний період та надасть рекомендації.
              </Typography>
            </EmptyState>
          ) : (
            <>
              {activeConversation.messages.map((message) => (
                <Box key={message.id}>
                  {message.role === 'user' && message.dateFrom && message.dateTo && (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end', 
                        mb: 0.5 
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        📅 {moment(message.dateFrom).format('DD.MM.YYYY')} - {moment(message.dateTo).format('DD.MM.YYYY')}
                      </Typography>
                    </Box>
                  )}
                  <MessageBubble isUser={message.role === 'user'}>
                    {message.role === 'user' ? (
                      <Typography>{message.content}</Typography>
                    ) : (
                      <ReactMarkdown>{message.content}</ReactMarkdown>
                    )}
                    
                    {message.dataUsed && Object.keys(message.dataUsed).length > 0 && (
                      <DataUsedBox>
                        <Box 
                          onClick={() => toggleDataExpanded(message.id)}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            cursor: 'pointer',
                            gap: 0.5
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            📊 Використані дані
                          </Typography>
                          {expandedDataIds.has(message.id) ? (
                            <ExpandLess fontSize="small" />
                          ) : (
                            <ExpandMore fontSize="small" />
                          )}
                        </Box>
                        <Collapse in={expandedDataIds.has(message.id)}>
                          <JsonPre>
                            {JSON.stringify(message.dataUsed, null, 2)}
                          </JsonPre>
                        </Collapse>
                      </DataUsedBox>
                    )}
                  </MessageBubble>
                </Box>
              ))}
              
              {loading && (
                <MessageBubble isUser={false}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography>Аналізую дані...</Typography>
                  </Box>
                </MessageBubble>
              )}
              
              <div ref={messagesEndRef} />
            </>
          )}
        </MessagesContainer>

        <InputContainer>
          <InputWrapper>
            <DateRangeBar>
              <CalendarMonth fontSize="small" color="action" />
              <Typography variant="caption" color="textSecondary">
                Період аналізу:
              </Typography>
              <TextField
                type="date"
                size="small"
                value={dateFrom}
                onChange={(e) => handleDateFromChange(e.target.value)}
                sx={{ 
                  '& .MuiInputBase-input': { py: 0.5, fontSize: 13 },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                }}
              />
              <Typography variant="caption" color="textSecondary">—</Typography>
              <TextField
                type="date"
                size="small"
                value={dateTo}
                onChange={(e) => handleDateToChange(e.target.value)}
                sx={{ 
                  '& .MuiInputBase-input': { py: 0.5, fontSize: 13 },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                }}
              />
              <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                (макс. 60 днів)
              </Typography>
            </DateRangeBar>
            
            <TextField
              fullWidth
              multiline
              maxRows={6}
              placeholder="Введіть питання для AI (наприклад: що покращити у роботі самокатів?)"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton 
                      onClick={handleSend}
                      disabled={!input.trim() || loading}
                      color="primary"
                    >
                      {loading ? <CircularProgress size={24} /> : <Send />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 3,
                  backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#1e1e1e',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                }
              }}
            />
            
            <Typography variant="caption" color="textSecondary" sx={{ textAlign: 'center' }}>
              Enter — відправити · Shift+Enter — новий рядок
            </Typography>
          </InputWrapper>
        </InputContainer>
      </ChatArea>
      
      <Dialog 
        open={promptDialogOpen} 
        onClose={() => setPromptDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Системний промпт AI
        </DialogTitle>
        <DialogContent>
          {loadingPrompt ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth
                multiline
                minRows={8}
                maxRows={20}
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="Введіть системний промпт для AI..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'monospace',
                    fontSize: 14
                  }
                }}
              />
              {promptUpdatedAt && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Останнє оновлення: {moment(promptUpdatedAt).format('DD.MM.YYYY HH:mm')}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromptDialogOpen(false)}>
            Скасувати
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSavePrompt}
            disabled={loadingPrompt || savingPrompt}
          >
            {savingPrompt ? <CircularProgress size={20} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default AiAnalyticsPage;
